export type Language = "sv" | "en";

export const translations = {
  // Navbar
  nav: {
    alert: {
      sv: "Regulatorisk Varning 2026",
      en: "2026 Regulatory Alert",
    },
    alertText: {
      sv: "— AMLA:s tekniska standarder träder i kraft 10 juli 2026",
      en: "— AMLA Technical Standards deadline: July 10, 2026",
    },
    services: { sv: "Tjänster", en: "Services" },
    about: { sv: "Om oss", en: "About" },
    caseStudies: { sv: "Kundcase", en: "Case Studies" },
    contact: { sv: "Kontakt", en: "Contact" },
    healthCheck: { sv: "Hälsokontroll", en: "Health Check" },
  },

  // Hero
  hero: {
    tag: {
      sv: "Finansiell brottslighet & regelefterlevnad",
      en: "Financial Crime & Compliance",
    },
    h1Pre: { sv: "Precision inom", en: "Precision in" },
    h1Accent: { sv: "Finansiell Brottslighet", en: "Financial Crime" },
    h1Post: { sv: "& Regelefterlevnad", en: "Compliance" },
    description: {
      sv: "Strategisk rådgivning för svenska finansiella aktörer — AML, regulatorisk rapportering och anpassning till det nya EU-regelverket.",
      en: "Strategic advisory for Swedish financial institutions navigating AML, regulatory reporting, and the evolving EU compliance landscape.",
    },
    cta1: { sv: "Boka bedömning", en: "Request Assessment" },
    cta2: { sv: "Se våra tjänster", en: "Explore Services" },
    stat1Value: { sv: "10+", en: "10+" },
    stat1Label: { sv: "Års erfarenhet av finansiell brottslighet", en: "Years in Financial Crime" },
    stat2Value: { sv: "Svensk", en: "Swedish" },
    stat2Label: { sv: "Marknadsspecialisering", en: "Market Specialisation" },
    stat3Value: { sv: "2026", en: "2026" },
    stat3Label: { sv: "Redo för AMLA & DORA", en: "AMLA & DORA Ready" },
  },

  // Services
  services: {
    tag: { sv: "Kärntjänster", en: "Core Services" },
    title: { sv: "Specialistrådgivning", en: "Specialist Advisory" },
    description: {
      sv: "Djup expertis inom hela compliance-området för finansiell brottslighet, skräddarsydd efter svenska förutsättningar.",
      en: "Deep expertise across the full spectrum of financial crime compliance, tailored to the Swedish regulatory environment.",
    },
    s1Title: { sv: "Periodisk rapportering (FIDAC)", en: "Periodic Reporting (FIDAC)" },
    s1Desc: {
      sv: "Vi stöttar svenska företag med den årliga FIDAC-inlämningen den 31 mars — korrekt, komplett och i tid till Finansinspektionen.",
      en: "Assisting Swedish firms with the annual March 31 FIDAC deadline — ensuring accurate, complete, and timely periodic reporting to Finansinspektionen.",
    },
    s1Tag: { sv: "Deadline: 31 mars", en: "Deadline: March 31" },
    s2Title: { sv: "MLRO-stöd", en: "MLRO Support" },
    s2Desc: {
      sv: "Interim- och strategisk rådgivning för reglerade verksamheter som behöver en erfaren penningtvättsansvarig — från gapanalys till full operativ täckning.",
      en: "Interim and strategic advisory for regulated entities seeking experienced Money Laundering Reporting Officer support, from gap analysis to full operational coverage.",
    },
    s2Tag: { sv: "Interim & Rådgivning", en: "Interim & Advisory" },
    s3Title: { sv: "Förklarbar AI-efterlevnad", en: "Explainable AI Compliance" },
    s3Desc: {
      sv: "Vi hjälper företag att validera AI-baserade system för transaktionsövervakning och säkerställer modellstyrning, förklarbarhet och regulatorisk försvarbarhet.",
      en: "Helping firms validate AI-driven transaction monitoring systems, ensuring model governance, explainability, and regulatory defensibility.",
    },
    s3Tag: { sv: "AI-styrning", en: "AI Governance" },
    s4Title: { sv: "AMLA- & DORA-anpassning", en: "AMLA & DORA Alignment" },
    s4Desc: {
      sv: "Vi förbereder finansiella institutioner för EU:s nya penningtvättsmyndighet (AMLA) och kraven i DORA — Digital Operational Resilience Act.",
      en: "Preparing financial institutions for the new EU Anti-Money Laundering Authority standards and the Digital Operational Resilience Act (DORA) requirements.",
    },
    s4Tag: { sv: "EU-reglering", en: "EU Regulation" },
  },

  // Remediation
  remediation: {
    tag: { sv: "Agil åtgärdshantering", en: "Agile Backlog Remediation" },
    title: { sv: "Åtgärdsteamet", en: "The Remediation" },
    titleAccent: { sv: "Strike Team", en: "Strike Team" },
    subtitle: {
      sv: "Vi eliminerar regulatorisk skuld — med kirurgisk precision.",
      en: "Eliminating regulatory debt with precision.",
    },
    problemLabel: { sv: "Problemet", en: "The Problem" },
    problemH1: { sv: "Regulatoriskt tryck.", en: "Regulatory Pressure." },
    problemH2: { sv: "Åldrade ärenden.", en: "Aged Alerts." },
    problemDesc: {
      sv: "Historiska eftersläpningar är mer än en operativ belastning — de utgör en högriskfaktor. Olösta KYC-ärenden och gamla transaktionsövervakningsärenden exponerar er institution för tillsynsåtgärder, ryktesrisk och betydande sanktionsavgifter.",
      en: "Historical backlogs are more than just an operational burden — they are a high-priority risk. Unresolved KYC files and aged transaction monitoring alerts expose your institution to regulatory action, reputational damage, and significant financial penalties.",
    },
    solutionLabel: { sv: "Lösningen", en: "The Solution" },
    solutionH1: { sv: "Forensisk åtgärdsprocess", en: "4-Step Forensic" },
    solutionH2: { sv: "i fyra steg.", en: "Remediation Process." },
    solutionDesc: {
      sv: "Vi sätter in specialiserade strike teams som arbetar igenom KYC- och TM-ärenden — varje fil uppdateras till 2026 års standard utan att störa den löpande verksamheten.",
      en: "We deploy specialized strike teams to clear KYC and Transaction Monitoring alerts, ensuring every file is brought up to 2026 standards without disrupting your business-as-usual operations.",
    },
    step1Title: { sv: "KYC-uppdatering", en: "KYC File Refresh" },
    step1Desc: {
      sv: "Systematisk genomgång och uppdatering av äldre kunddata, UBO-verifiering och dokumentation av medlens ursprung (SoW).",
      en: "Systematic review and update of legacy customer data, UBO verification, and Source of Wealth (SoW) documentation.",
    },
    step2Title: { sv: "SAR/STR-hantering", en: "SAR/STR Backlog Clearing" },
    step2Desc: {
      sv: "Forensisk utredning av åldrade ärenden och framtagning av kvalificerade misstankerapporter till Finanspolisen.",
      en: "Forensic investigation of aged alerts and drafting of high-quality Suspicious Activity Reports for the Financial Intelligence Unit.",
    },
    step3Title: { sv: "Sanktions- och PEP-åtgärder", en: "Sanctions & PEP Remediation" },
    step3Desc: {
      sv: 'Omscreening av hela kundbasen mot aktuella 2026-listor med hjälp av förklarbar AI-logik.',
      en: 'Re-screening of entire customer bases against the latest 2026 global lists using "Explainable AI" logic.',
    },
    step4Title: { sv: "Revisionsfärdiga underlag", en: "Audit-Ready Evidence Trails" },
    step4Desc: {
      sv: "Varje åtgärdad fil innehåller en tidsstämplad beslutslogg — redo för nästa FI-granskning.",
      en: "Every remediated file includes a timestamped decision log, ready for the next FI inspection.",
    },
    cta: { sv: "Aktivera ert Strike Team", en: "Deploy Your Strike Team" },
  },

  // Case Studies
  caseStudies: {
    tag: { sv: "Kundcase", en: "Case Studies" },
    title: { sv: "Bevisade resultat", en: "Proven Results" },
    subtitle: {
      sv: "Verkliga uppdrag där vi levererat mätbara resultat åt reglerade verksamheter.",
      en: "Real-world engagements where we delivered measurable outcomes for regulated firms.",
    },

    // Case 1
    cs1Tag: { sv: "Strike Team-åtgärd", en: "Strike Team Remediation" },
    cs1Title: {
      sv: "Snabb åtgärd av 1 500 KYC-ärenden",
      en: "Rapid Remediation of a 1,500-File KYC Backlog",
    },
    cs1ChallengeLabel: { sv: "Utmaningen", en: "The Challenge" },
    cs1Challenge: {
      sv: "En nordisk fintech stod inför en kritisk situation: över 1 500 högriskfiler för företagskunder hade passerat sin periodiska granskningsperiod, och tre månaders obearbetade transaktionsövervakningsärenden hade ackumulerats. Med en FI-inspektion på ingång var det interna compliance-teamet överbelastat.",
      en: "A mid-sized Nordic Fintech faced a critical \"Regulatory Debt\" event: 1,500+ high-risk corporate files had exceeded their periodic review date, and a three-month backlog of unreviewed transaction monitoring alerts had accumulated. With an FI inspection looming, the internal compliance team was overextended.",
    },
    cs1ActionLabel: { sv: "Åtgärden", en: "The Action" },
    cs1ActionIntro: {
      sv: "Lloyd Consulting AB anlitades som interim-ansvarig. Vi aktiverade ett specialiserat Strike Team-arbetsflöde:",
      en: "Lloyd Consulting AB was engaged as an interim lead. We deployed a specialized \"Strike Team\" workflow:",
    },
    cs1Step1Title: { sv: "Riskbaserad prioritering", en: "Risk-Based Triage" },
    cs1Step1Desc: {
      sv: "Vi prioriterade filer efter högexponerade jurisdiktioner och transaktionsvolym.",
      en: "We prioritized files by high-exposure jurisdictions and transaction volume.",
    },
    cs1Step2Title: { sv: "Forensisk CDD", en: "Forensic CDD" },
    cs1Step2Desc: {
      sv: "Djupgående UBO-verifiering (verklig huvudman) och validering av medlens ursprung (SoW) för komplexa företagsstrukturer.",
      en: "Performed deep-dive UBO (Ultimate Beneficial Owner) verification and Source of Wealth (SoW) validation for complex corporate structures.",
    },
    cs1Step3Title: { sv: "Rensning & Stängning", en: "Clean-up & Closure" },
    cs1Step3Desc: {
      sv: 'Implementerade en "One-Touch"-granskningsprocess som säkerställde att varje åtgärdad fil uppfyllde 2026 års AMLA-standard.',
      en: 'Implemented a "One-Touch" review process, ensuring every remediated file met 2026 AMLA standards.',
    },
    cs1ResultLabel: { sv: "Resultatet", en: "The Result" },
    cs1Result1: {
      sv: "100 % stängning — alla eftersläpningar åtgärdade inom 12 veckor.",
      en: "100% Closure — the backlog was fully remediated within 12 weeks.",
    },
    cs1Result2: {
      sv: "Revisionsredo — företaget klarade FI-inspektionen utan anmärkningar relaterade till åldrade ärenden.",
      en: "Audit Readiness — the firm successfully passed its FI inspection with zero findings related to aged alerts.",
    },
    cs1Result3: {
      sv: 'Hållbar löpande drift — implementerade ett nytt "Perpetual KYC"-protokoll för att förhindra framtida eftersläpningar.',
      en: 'Sustainable BAU — provided a new "Perpetual KYC" protocol to prevent future backlog accumulation.',
    },

    // Case 2
    cs2Tag: { sv: "Forensisk utredning", en: "Forensic Investigation" },
    cs2Title: {
      sv: "Utredning av komplexa SAR/STR-eftersläpningar",
      en: "Resolving Complex Suspicious Activity Backlogs",
    },
    cs2ChallengeLabel: { sv: "Utmaningen", en: "The Challenge" },
    cs2Challenge: {
      sv: "En Tier 2-bank upplevde en kraftig ökning av falsklarm från ett nyligen implementerat AI-övervakningssystem, vilket ledde till 400+ outredda ärenden involverade gränsöverskridande digitala tillgångsöverföringar.",
      en: "A Tier-2 bank experienced a surge in \"False Positives\" from a newly implemented AI-monitoring system, leading to 400+ uninvestigated alerts involving cross-border digital asset transfers.",
    },
    cs2ActionLabel: { sv: "Åtgärden", en: "The Action" },
    cs2ActionIntro: {
      sv: "Viktor Lloyd tillhandahöll interim-specialiststöd för att leda utredningsarbetet:",
      en: "Viktor Lloyd provided interim specialist support to lead the investigative effort:",
    },
    cs2Step1Title: { sv: "Nätverksanalys", en: "Network Analysis" },
    cs2Step1Desc: {
      sv: 'Avancerad länkanalys för att identifiera "smurfing"-mönster över 40+ relaterade konton.',
      en: 'Used advanced link analysis to identify "Smurfing" patterns across 40+ related accounts.',
    },
    cs2Step2Title: { sv: "Kvalificerad rapportering", en: "High-Quality Reporting" },
    cs2Step2Desc: {
      sv: "Upprättade och lämnade in 12 prioriterade misstankerapporter (SAR) till Finanspolisen med det forensiska djup som krävs för att stödja brottsbekämpning.",
      en: "Drafted and filed 12 high-priority Suspicious Activity Reports (SARs) with Finanspolisen, providing the required forensic detail to support law enforcement action.",
    },
    cs2Step3Title: { sv: "Systemkalibrering", en: "System Calibration" },
    cs2Step3Desc: {
      sv: "Rådgav bankens IT-team om finjustering av alertlogik för att minska framtida falsklarm med 40 %.",
      en: "Advised the bank's IT team on refining alert logic to reduce future false positives by 40%.",
    },
    cs2ResultLabel: { sv: "Resultatet", en: "The Result" },
    cs2Result: {
      sv: "Utredningseftersläpningen åtgärdades inom 30 dagar och bankens relation med Finanspolisen stärktes genom överlägsen rapporteringskvalitet.",
      en: "Cleared the investigative backlog in 30 days and strengthened the bank's relationship with the Financial Intelligence Unit (FIU) through superior reporting quality.",
    },

    // Process
    processTitle: { sv: "Vår process", en: "Our Process" },
    processStep1: { sv: "Triage & Analys", en: "Triage & Analysis" },
    processStep1Desc: { sv: "Prioritera ärenden efter risk", en: "Prioritize cases by risk" },
    processStep2: { sv: "Forensisk granskning", en: "Forensic Review" },
    processStep2Desc: { sv: "Djupgående utredning", en: "Deep-dive investigation" },
    processStep3: { sv: "Åtgärd & Stängning", en: "Remediate & Close" },
    processStep3Desc: { sv: "Uppfyll 2026-standard", en: "Meet 2026 standards" },
    processStep4: { sv: "Revisionsfärdig", en: "Audit-Ready" },
    processStep4Desc: { sv: "Dokumentation & överlämnande", en: "Documentation & handover" },
  },

  // About
  about: {
    tag: { sv: "Om oss", en: "About" },
    title: { sv: "Grundarens profil", en: "Founder's Profile" },
    role: { sv: "Grundare & Huvudkonsult", en: "Founder & Principal Consultant" },
    bio: {
      sv: "Med över ett decenniums praktisk erfarenhet av compliance inom finansiell brottslighet hos Sveriges ledande finansiella institutioner har Viktor Lloyd byggt en djup expertis inom AML, regulatorisk teknik och EU:s compliance-ramverk. Genom att kombinera strategisk insikt med operativ precision hjälper han verksamheter att inte bara möta regulatoriska krav — utan att bygga genuint motståndskraftiga compliance-program.",
      en: "With over a decade of hands-on experience in financial crime compliance across Sweden's leading financial institutions, Viktor Lloyd brings deep domain expertise in AML, regulatory technology, and EU compliance frameworks. His approach combines strategic insight with operational precision — helping firms not just meet regulatory demands, but build genuinely resilient compliance programmes.",
    },
    expertiseTitle: { sv: "Nyckelkompetenser", en: "Key Expertise" },
    e1: { sv: "Design & implementering av AML/CFT-ramverk", en: "AML/CFT Framework Design & Implementation" },
    e2: { sv: "Svensk regulatorisk efterlevnad (Finansinspektionen)", en: "Swedish Regulatory Compliance (Finansinspektionen)" },
    e3: { sv: "Styrning av AI-driven transaktionsövervakning", en: "AI-Driven Transaction Monitoring Governance" },
    e4: { sv: "Periodisk rapportering & FIDAC-inlämningar", en: "Periodic Reporting & FIDAC Submissions" },
    e5: { sv: "Beredskapsanalyser för AMLA & DORA", en: "AMLA & DORA Readiness Assessments" },
    e6: { sv: "MLRO-rådgivning & teamutveckling", en: "MLRO Advisory & Team Development" },
    credentialsTitle: { sv: "Meriter & Förtroende", en: "Credentials & Trust" },
    c1: {
      sv: "ACAMS Transaction Monitoring Foundations Certificate & ICA Advanced Certificate in Practical Due Diligence",
      en: "ACAMS Transaction Monitoring Foundations Certificate & ICA Advanced Certificate in Practical Due Diligence",
    },
    c2: {
      sv: "Kandidatexamen i företagsekonomi, Lunds universitet",
      en: "B.Sc. in Business & Economics from Lund University",
    },
    c3: {
      sv: "Aktiv bidragsgivare till den nordiska compliance-gemenskapen och rådgivare till finansiella nav i Malmö och Stockholm",
      en: "Active contributor to the Nordic compliance community and advisor to Malmö/Stockholm based financial hubs",
    },
    prevExp: { sv: "Tidigare erfarenhet", en: "Previous Experience" },
  },

  // Contact
  contact: {
    tag: { sv: "Kontakt", en: "Contact" },
    title: { sv: "Hör av dig", en: "Get in Touch" },
    description: {
      sv: "Konfidentiella förfrågningar välkomnas. All kommunikation hanteras med största diskretion.",
      en: "Confidential enquiries welcome. All communications are handled with the utmost discretion.",
    },
    name: { sv: "Namn", en: "Name" },
    namePlaceholder: { sv: "Ditt namn", en: "Your name" },
    email: { sv: "E-post", en: "Email" },
    emailPlaceholder: { sv: "din@email.com", en: "your@email.com" },
    subject: { sv: "Ämne", en: "Subject" },
    subjectPlaceholder: { sv: "Hur kan vi hjälpa er?", en: "How can we help?" },
    message: { sv: "Meddelande", en: "Message" },
    messagePlaceholder: {
      sv: "Berätta kort om era compliance-behov...",
      en: "Tell us about your compliance needs...",
    },
    send: { sv: "Skicka meddelande", en: "Send Message" },
    location: { sv: "Malmö, Sverige", en: "Malmö, Sweden" },
    confidential: { sv: "Konfidentiella förfrågningar", en: "Confidential Enquiries" },
  },

  // Footer
  footer: {
    gdpr: { sv: "GDPR-policy", en: "GDPR Policy" },
  },

  // Health Check
  healthCheck: {
    title: { sv: "Compliance-hälsokontroll", en: "Compliance Health Check" },
    step1Title: { sv: "Er organisation", en: "Your Organisation" },
    step1Desc: { sv: "Berätta om ert företag", en: "Tell us about your firm" },
    step2Title: { sv: "Compliance-behov", en: "Compliance Needs" },
    step2Desc: { sv: "Vilka utmaningar står ni inför?", en: "What challenges are you facing?" },
    step3Title: { sv: "Kontaktuppgifter", en: "Get Your Assessment" },
    step3Desc: { sv: "Hur når vi er?", en: "How can we reach you?" },
    companyName: { sv: "Företagsnamn", en: "Company Name" },
    companyPlaceholder: { sv: "Ert företagsnamn", en: "Your company name" },
    industry: { sv: "Bransch", en: "Industry" },
    industryPlaceholder: { sv: "Välj bransch", en: "Select your industry" },
    banking: { sv: "Bank", en: "Banking" },
    fintech: { sv: "Fintech", en: "Fintech" },
    insurance: { sv: "Försäkring", en: "Insurance" },
    payment: { sv: "Betaltjänster", en: "Payment Services" },
    investment: { sv: "Kapitalförvaltning", en: "Investment Management" },
    otherEntity: { sv: "Annan reglerad verksamhet", en: "Other Regulated Entity" },
    employees: { sv: "Antal anställda", en: "Number of Employees" },
    employeesPlaceholder: { sv: "Välj intervall", en: "Select range" },
    primaryChallenge: { sv: "Primär compliance-utmaning", en: "Primary Compliance Challenge" },
    challengePlaceholder: { sv: "Välj utmaning", en: "Select challenge" },
    aml: { sv: "AML/CFT-ramverk", en: "AML/CFT Framework" },
    reporting: { sv: "Regulatorisk rapportering (FIDAC)", en: "Regulatory Reporting (FIDAC)" },
    ai: { sv: "AI-modellstyrning", en: "AI Model Governance" },
    amla: { sv: "AMLA-förberedelse", en: "AMLA Preparation" },
    dora: { sv: "DORA-efterlevnad", en: "DORA Compliance" },
    mlro: { sv: "MLRO-rekrytering/-stöd", en: "MLRO Recruitment/Support" },
    describeSituation: { sv: "Beskriv er situation", en: "Describe Your Situation" },
    descPlaceholder: {
      sv: "Kort beskrivning av era nuvarande compliance-behov...",
      en: "Brief description of your current compliance needs...",
    },
    fullName: { sv: "Fullständigt namn", en: "Full Name" },
    fullNamePlaceholder: { sv: "Ditt namn", en: "Your name" },
    emailLabel: { sv: "E-post", en: "Email" },
    emailPlaceholder: { sv: "din@email.com", en: "your@email.com" },
    phone: { sv: "Telefon (valfritt)", en: "Phone (Optional)" },
    phonePlaceholder: { sv: "+46...", en: "+46..." },
    step: { sv: "Steg", en: "Step" },
    back: { sv: "Tillbaka", en: "Back" },
    next: { sv: "Nästa", en: "Next" },
    submit: { sv: "Skicka", en: "Submit" },
    successTitle: { sv: "Förfrågan mottagen", en: "Assessment Request Received" },
    successDesc: {
      sv: "Vi granskar er förfrågan och återkommer inom 24 timmar.",
      en: "We'll review your submission and respond within 24 hours.",
    },
  },
} as const;

export function t(
  entry: { sv: string; en: string },
  lang: Language
): string {
  return entry[lang];
}
